/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { createContext, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Radio } from 'lucide-react';
import useLiveSession from '../hooks/useLiveSession';
import toastService from '../utils/toastService';
import { getLiveSessionNamespace } from '../utils/scope';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionContext';

const LiveSessionContext = createContext(null);

const getDisplayName = (session) => session.creator_name || session.device_name || session.room;

const NewSessionToast = ({ session, joinedRoom, onJoin, toastId }) => {
  const busy = !!joinedRoom && joinedRoom !== session.room;

  const handleJoin = async () => {
    try {
      await onJoin(session);
      toast.dismiss(toastId);
    } catch {
      toastService.error('Failed to join the live session');
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Radio size={18} className="flex-shrink-0 text-primary" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate">New live session</div>
        <div className="text-xs opacity-70 truncate">{getDisplayName(session)}</div>
      </div>
      <button
        type="button"
        onClick={handleJoin}
        disabled={busy}
        className="btn btn-xs btn-primary disabled:opacity-30 flex-shrink-0"
        title={busy ? 'Leave your current session first' : 'Join session'}
      >
        {busy ? 'Busy' : 'Join'}
      </button>
    </div>
  );
};

NewSessionToast.propTypes = {
  session: PropTypes.object.isRequired,
  joinedRoom: PropTypes.string,
  onJoin: PropTypes.func.isRequired,
  toastId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export const LiveSessionProvider = ({ children }) => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const active = !!user && can.service_requests.view();
  const namespace = getLiveSessionNamespace(user);

  const liveSession = useLiveSession(active, user?.displayName, namespace);
  const { sessions, joinedRoom, join } = liveSession;
  const knownRoomsRef = useRef(new Map());
  const toastedRoomsRef = useRef(new Set());

  useEffect(() => {
    const knownRooms = knownRoomsRef.current;

    if (!active) {
      knownRooms.forEach((_status, room) => toast.dismiss(`live-session-${room}`));
      knownRoomsRef.current = new Map();
      toastedRoomsRef.current = new Set();
      return;
    }

    const currentRoomStatus = new Map(sessions.map((s) => [s.room, s.status]));

    knownRooms.forEach((_prevStatus, room) => {
      const currentStatus = currentRoomStatus.get(room);
      if (currentStatus === undefined || currentStatus === 'in_live_session') {
        toast.dismiss(`live-session-${room}`);
        toastedRoomsRef.current.delete(room);
      }
    });

    const newlyJoinable = sessions.filter((s) => (
      s.status !== 'in_live_session' && !knownRooms.has(s.room)
    ));

    newlyJoinable.forEach((session) => {
      const toastId = `live-session-${session.room}`;
      toastedRoomsRef.current.add(session.room);
      toast(
        ({ closeToast }) => (
          <NewSessionToast
            session={session}
            joinedRoom={joinedRoom}
            onJoin={join}
            toastId={toastId}
          />
        ),
        {
          toastId,
          autoClose: 8000,
          closeOnClick: false,
          onClose: () => toastedRoomsRef.current.delete(session.room),
        }
      );
    });

    knownRoomsRef.current = currentRoomStatus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, active]);

  // Existing session toasts capture `joinedRoom` at creation time; refresh
  // their rendered content whenever it changes so a stale toast doesn't
  // keep offering "Join" once the user is already in another session.
  useEffect(() => {
    toastedRoomsRef.current.forEach((room) => {
      const session = sessions.find((s) => s.room === room);
      if (!session) {
        return;
      }
      const toastId = `live-session-${room}`;
      toast.update(toastId, {
        render: ({ closeToast }) => (
          <NewSessionToast
            session={session}
            joinedRoom={joinedRoom}
            onJoin={join}
            toastId={toastId}
          />
        ),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinedRoom]);

  return (
    <LiveSessionContext.Provider value={liveSession}>
      {children}
    </LiveSessionContext.Provider>
  );
};

LiveSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLiveSessionContext = () => {
  const context = useContext(LiveSessionContext);
  if (!context) {
    throw new Error('useLiveSessionContext must be used within a LiveSessionProvider');
  }
  return context;
};
